/**
 * Customer Service
 * 
 * Handles all customer-related business logic including:
 * - Customer creation with validation
 * - Customer updates with data normalization
 * - Customer search and filtering
 * - Customer statistics and reporting
 * - Customer data enrichment
 * - Event emission for integrations
 * 
 * @example
 * ```typescript
 * const customerService = new CustomerService();
 * 
 * // Create a new customer
 * const customer = await customerService.createCustomer({
 *   first_name: 'John',
 *   last_name: 'Doe',
 *   email: 'john@example.com',
 *   phone_number: '555-1234',
 *   company_id: 'hales-ac'
 * });
 * 
 * // Search customers
 * const results = await customerService.searchCustomers('john', {
 *   commercial: false,
 *   limit: 10
 * });
 * 
 * // Get customer dashboard data
 * const dashboard = await customerService.getCustomerDashboard('customer-123');
 * ```
 */

import { BaseService, ServiceError, ValidationSchema, CommonSchemas } from './BaseService';
import { repositories } from '../repositories';
import { Customer, CustomerFilters } from '../repositories/CustomerRepository';
import { QueryOptions } from '../repositories/BaseRepository';

/**
 * Input for creating a new customer
 */
export interface CreateCustomerInput {
  first_name?: string;
  last_name?: string;
  company_name?: string;
  created_by_company_id: string;
  email?: string;
  phone_number?: string;
  commercial?: boolean;
  address_1?: string;
  address_2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
}

/**
 * Input for updating a customer
 */
export interface UpdateCustomerInput {
  first_name?: string;
  last_name?: string;
  company_name?: string;
  email?: string;
  phone_number?: string;
  commercial?: boolean;
  address_1?: string;
  address_2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  balance_due?: number;
}

/**
 * Customer dashboard data
 */
export interface CustomerDashboard {
  customer: Customer;
  recentActivity: {
    agreements: number;
    openJobs: number;
    totalJobs: number;
    lastJobDate: string | null;
  };
  financial: {
    balanceDue: number;
    totalPaid: number;
    outstandingInvoices: number;
  };
  equipment: {
    total: number;
    underWarranty: number;
    needingService: number;
  };
}

/**
 * Customer Service Class
 */
export class CustomerService extends BaseService {
  /**
   * Validation schema for creating customers
   */
  private createCustomerSchema: ValidationSchema = {
    created_by_company_id: {
      required: true,
      type: 'string',
      message: 'Company ID is required'
    },
    first_name: {
      type: 'string',
      min: 1,
      max: 100,
      message: 'First name must be 1-100 characters'
    },
    last_name: {
      type: 'string',
      min: 1,
      max: 100,
      message: 'Last name must be 1-100 characters'
    },
    company_name: {
      type: 'string',
      min: 1,
      max: 200,
      message: 'Company name must be 1-200 characters'
    },
    email: CommonSchemas.email,
    phone_number: CommonSchemas.phone,
    zip_code: CommonSchemas.zip,
    commercial: {
      type: 'boolean',
      message: 'Commercial must be true or false'
    }
  };

  /**
   * Create a new customer
   */
  async createCustomer(input: CreateCustomerInput): Promise<Customer> {
    try {
      // Validate input
      this.validate(input, this.createCustomerSchema);

      // Additional business logic validation
      if (!input.first_name && !input.last_name && !input.company_name) {
        throw ServiceError.validation([{
          field: 'name',
          message: 'Either personal name or company name is required'
        }]);
      }

      // Check for duplicate email
      if (input.email) {
        const existing = await repositories.customers.findByEmail(input.email);
        if (existing) {
          throw ServiceError.conflict('A customer with this email already exists');
        }
      }

      // Normalize and prepare data
      const customerData = this.prepareCustomerData(input);

      // Create customer
      const customer = await repositories.customers.create(customerData);

      // Emit event for integrations
      await this.emit('customer.created', {
        customerId: customer.id,
        companyId: customer.created_by_company_id,
        customer
      });

      return customer;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Update an existing customer
   */
  async updateCustomer(
    customerId: string,
    input: UpdateCustomerInput
  ): Promise<Customer> {
    try {
      // Validate input
      this.validate(input, {
        first_name: this.createCustomerSchema.first_name,
        last_name: this.createCustomerSchema.last_name,
        company_name: this.createCustomerSchema.company_name,
        email: this.createCustomerSchema.email,
        phone_number: this.createCustomerSchema.phone_number,
        zip_code: this.createCustomerSchema.zip_code,
        commercial: this.createCustomerSchema.commercial
      });

      // Check if customer exists
      const existingCustomer = await repositories.customers.findById(customerId);
      if (!existingCustomer) {
        throw ServiceError.notFound('Customer', customerId);
      }

      // Check for duplicate email if changing
      if (input.email && input.email !== existingCustomer.email) {
        const duplicate = await repositories.customers.findByEmail(input.email);
        if (duplicate) {
          throw ServiceError.conflict('A customer with this email already exists');
        }
      }

      // Normalize and prepare data
      const customerData = this.prepareCustomerData(input);

      // Update customer
      const customer = await repositories.customers.update(customerId, customerData);

      // Emit event
      await this.emit('customer.updated', {
        customerId: customer.id,
        companyId: customer.created_by_company_id,
        customer,
        changes: input
      });

      return customer;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get customer by ID
   */
  async getCustomer(customerId: string): Promise<Customer> {
    try {
      const customer = await repositories.customers.findById(customerId);
      
      if (!customer) {
        throw ServiceError.notFound('Customer', customerId);
      }

      return customer;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Search customers
   */
  async searchCustomers(
    query: string,
    filters?: Omit<CustomerFilters, 'search'>,
    options?: QueryOptions
  ): Promise<Customer[]> {
    try {
      return await repositories.customers.findByFilters(
        { ...filters, search: query },
        options
      );
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get customers by filters
   */
  async getCustomers(
    filters?: CustomerFilters,
    options?: QueryOptions
  ): Promise<Customer[]> {
    try {
      return await repositories.customers.findByFilters(filters || {}, options);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get customer statistics by company
   */
  async getCompanyStats(): Promise<{
    halesAC: number;
    ccs: number;
    abrahamAC: number;
    rightAwayHomeWarranty: number;
  }> {
    try {
      // Get actual company UUIDs from sw_companies table
      const breakdown = await repositories.customers.getCompanyBreakdown();
      
      // Map company names to camelCase keys expected by the UI
      return {
        halesAC: breakdown['Hales AC'] || 0,
        ccs: breakdown['Climate Control Services'] || 0,
        abrahamAC: breakdown['Abraham AC'] || 0,
        rightAwayHomeWarranty: breakdown['Right Away Home Warranty'] || 0,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get customer dashboard data
   */
  async getCustomerDashboard(customerId: string): Promise<CustomerDashboard> {
    try {
      // Get customer
      const customer = await this.getCustomer(customerId);

      // Get agreements
      const agreements = await repositories.agreements.findByCustomer(customerId);
      const activeAgreements = agreements.filter(a => a.status === 'active').length;

      // Get equipment
      const equipment = await repositories.equipment.findByCustomer(customerId);
      const underWarranty = equipment.filter(e => e.is_under_warranty).length;
      const needingService = equipment.filter(e => 
        e.next_service_due && new Date(e.next_service_due) <= new Date()
      ).length;

      // Build dashboard
      return {
        customer,
        recentActivity: {
          agreements: activeAgreements,
          openJobs: customer.open_jobs_count || 0,
          totalJobs: customer.total_jobs_count || 0,
          lastJobDate: customer.last_job_date
        },
        financial: {
          balanceDue: customer.balance_due || 0,
          totalPaid: 0, // TODO: Calculate from payment history
          outstandingInvoices: 0 // TODO: Calculate from invoices
        },
        equipment: {
          total: equipment.length,
          underWarranty,
          needingService
        }
      };
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get customers with outstanding balances
   */
  async getCustomersWithBalance(minBalance: number = 0): Promise<Customer[]> {
    try {
      return await repositories.customers.findWithBalance(minBalance);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get customers by company
   */
  async getCustomersByCompany(
    companyId: string | null,
    options?: QueryOptions
  ): Promise<Customer[]> {
    try {
      return await repositories.customers.findByCompany(companyId, options);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Merge duplicate customers
   */
  async mergeCustomers(
    primaryCustomerId: string,
    duplicateCustomerId: string
  ): Promise<Customer> {
    try {
      // Get both customers
      const primaryCustomer = await this.getCustomer(primaryCustomerId);
      const duplicateCustomer = await this.getCustomer(duplicateCustomerId);

      // Validate they're from the same company
      if (primaryCustomer.created_by_company_id !== duplicateCustomer.created_by_company_id) {
        throw ServiceError.validation([{
          field: 'created_by_company_id',
          message: 'Cannot merge customers from different companies'
        }]);
      }

      // Merge data (prefer primary, fallback to duplicate)
      const mergedData: UpdateCustomerInput = {
        first_name: primaryCustomer.first_name || duplicateCustomer.first_name || undefined,
        last_name: primaryCustomer.last_name || duplicateCustomer.last_name || undefined,
        company_name: primaryCustomer.company_name || duplicateCustomer.company_name || undefined,
        email: primaryCustomer.email || duplicateCustomer.email || undefined,
        phone_number: primaryCustomer.phone_number || duplicateCustomer.phone_number || undefined,
        address_1: primaryCustomer.address_1 || duplicateCustomer.address_1 || undefined,
        address_2: primaryCustomer.address_2 || duplicateCustomer.address_2 || undefined,
        city: primaryCustomer.city || duplicateCustomer.city || undefined,
        state: primaryCustomer.state || duplicateCustomer.state || undefined,
        zip_code: primaryCustomer.zip_code || duplicateCustomer.zip_code || undefined,
        commercial: primaryCustomer.commercial,
        balance_due: (primaryCustomer.balance_due || 0) + (duplicateCustomer.balance_due || 0)
      };

      // Update primary customer
      const mergedCustomer = await this.updateCustomer(primaryCustomerId, mergedData);

      // TODO: Reassign all agreements, equipment, etc. from duplicate to primary

      // Emit merge event
      await this.emit('customer.merged', {
        primaryCustomerId,
        duplicateCustomerId,
        mergedCustomer
      });

      return mergedCustomer;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Delete a customer (soft delete by setting inactive)
   */
  async deleteCustomer(customerId: string): Promise<void> {
    try {
      // Check if customer exists
      const customer = await this.getCustomer(customerId);

      // Check if customer has active agreements
      const agreements = await repositories.agreements.findByCustomer(customerId);
      const activeAgreements = agreements.filter(a => a.status === 'active');
      
      if (activeAgreements.length > 0) {
        throw ServiceError.conflict(
          'Cannot delete customer with active agreements. Cancel agreements first.'
        );
      }

      // Soft delete (could add an 'active' field to Customer type)
      // For now, just emit event
      await this.emit('customer.deleted', {
        customerId,
        customer
      });

      // TODO: Implement actual soft delete when Customer type has 'active' field
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Find customer by phone number (returns first match)
   */
  async findCustomerByPhone(phoneNumber: string): Promise<Customer | null> {
    try {
      const customers = await repositories.customers.findByPhone(phoneNumber);
      return customers.length > 0 ? customers[0] : null;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Upsert customer by phone number
   * Updates existing customer or creates new one if not found
   * Used by agent console during live calls
   */
  async upsertCustomerByPhone(data: {
    phone: string;
    name?: string;
    email?: string;
    address?: string;
    companyId?: string;
  }): Promise<Customer> {
    try {
      // Find existing customer by phone
      const existingCustomer = await this.findCustomerByPhone(data.phone);

      if (existingCustomer) {
        // Update existing customer
        const updateData: UpdateCustomerInput = {};
        
        if (data.name) {
          // Split name into first and last
          const nameParts = data.name.trim().split(' ');
          if (nameParts.length > 0) {
            updateData.first_name = nameParts[0];
            if (nameParts.length > 1) {
              updateData.last_name = nameParts.slice(1).join(' ');
            }
          }
        }
        
        if (data.email) updateData.email = data.email;
        if (data.address) updateData.address_1 = data.address;

        return await this.updateCustomer(existingCustomer.id, updateData);
      } else {
        // Create new customer
        const createData: CreateCustomerInput = {
          phone_number: data.phone,
          created_by_company_id: data.companyId || 'hales-ac', // Default company
        };

        if (data.name) {
          const nameParts = data.name.trim().split(' ');
          if (nameParts.length > 0) {
            createData.first_name = nameParts[0];
            if (nameParts.length > 1) {
              createData.last_name = nameParts.slice(1).join(' ');
            }
          }
        }

        if (data.email) createData.email = data.email;
        if (data.address) createData.address_1 = data.address;

        return await this.createCustomer(createData);
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Prepare customer data for database (normalization)
   */
  private prepareCustomerData(
    input: CreateCustomerInput | UpdateCustomerInput
  ): Partial<Customer> {
    const data: Partial<Customer> = {};

    // Sanitize strings
    if (input.first_name) {
      const sanitized = this.sanitizeString(input.first_name);
      if (sanitized) data.first_name = sanitized;
    }
    if (input.last_name) {
      const sanitized = this.sanitizeString(input.last_name);
      if (sanitized) data.last_name = sanitized;
    }
    if (input.company_name) {
      const sanitized = this.sanitizeString(input.company_name);
      if (sanitized) data.company_name = sanitized;
    }
    if (input.address_1) {
      const sanitized = this.sanitizeString(input.address_1);
      if (sanitized) data.address_1 = sanitized;
    }
    if (input.address_2) {
      const sanitized = this.sanitizeString(input.address_2);
      if (sanitized) data.address_2 = sanitized;
    }
    if (input.city) {
      const sanitized = this.sanitizeString(input.city);
      if (sanitized) data.city = sanitized;
    }
    if (input.state) {
      const sanitized = this.sanitizeString(input.state);
      if (sanitized) data.state = sanitized;
    }

    // Normalize email
    if (input.email) {
      const normalized = this.normalizeEmail(input.email);
      if (normalized) data.email = normalized;
    }

    // Normalize phone
    if (input.phone_number) {
      const normalized = this.normalizePhone(input.phone_number);
      if (normalized) data.phone_number = normalized;
    }

    // Normalize ZIP
    if (input.zip_code) data.zip_code = input.zip_code.replace(/[^\d-]/g, '');

    // Build full name
    if (data.first_name || data.last_name) {
      data.full_name = [data.first_name, data.last_name]
        .filter(Boolean)
        .join(' ') || null;
    }

    // Build full address
    const addressParts = [
      data.address_1,
      data.address_2,
      data.city,
      data.state,
      data.zip_code
    ].filter(Boolean);
    
    if (addressParts.length > 0) {
      data.full_address = addressParts.join(', ');
    }

    // Copy other fields
    if ('commercial' in input) data.commercial = input.commercial || false;
    if ('balance_due' in input) data.balance_due = input.balance_due;
    if ('created_by_company_id' in input) data.created_by_company_id = (input as CreateCustomerInput).created_by_company_id;

    return data;
  }
}

/**
 * Singleton instance
 */
export const customerService = new CustomerService();
